// Third-party
import React from 'react';
import classNames from 'classnames';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import throttle from 'lodash/throttle';

// Components
import ColorPicker from '@/views/components/misc/ColorPicker';
import { HoldingIcon } from '../../../components/common';
import { Tooltip } from '../../../components/tooltips';

// Utils
import ComparisonItem from '@/types/comparison-item';
import InvisibleButton from '@/views/components/buttons/InvisibleButton';
import Nullable from '@/types/nullable';
import { formatMessage } from '../../../../utils/i18n';
import { Placement } from 'react-bootstrap/esm/types';
import { VALUE_ESCAPE } from 'keycode-js';

type Props = {
  className?: string;
  disableColorPicker?: boolean;
  item: ComparisonItem;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
};

type State = {
  placement: Placement;
  showColorPicker: boolean;
};

class LegacyComparisonCardHoldingIcon extends React.Component<Props, State> {
  private anchorRef = React.createRef<HTMLDivElement>();

  private holdingIconRef = React.createRef<HTMLButtonElement>();

  private popoverRef = React.createRef<HTMLDivElement>();

  private getScrollbarContainerLeft = (): number => {
    const { scrollbarContainerRef } = this.props;

    if (scrollbarContainerRef?.offsetLeft !== undefined) {
      return scrollbarContainerRef.offsetLeft;
    }

    const comparisonScrollbarContainer = document.querySelector('.comparison-scrollbar-container') as HTMLDivElement;
    return comparisonScrollbarContainer?.getBoundingClientRect()?.left ?? 0;
  };

  private shouldHideColorPicker = (scrollbarContainerLeft: number): boolean => {
    const { showColorPicker } = this.state;
    const holdingIcon = this.holdingIconRef.current;

    if (!showColorPicker || !holdingIcon) {
      return false;
    }

    const holdingIconLeft = holdingIcon.getBoundingClientRect().left;
    return holdingIconLeft < scrollbarContainerLeft;
  };

  readonly state: State = {
    placement: 'top',
    showColorPicker: false,
  };

  handleScroll = throttle(() => {
    const scrollbarContainerLeft = this.getScrollbarContainerLeft();
    this.setPopoverPlacement();
    // Hide color picker if it scrolls underneath comparison table labels
    if (this.shouldHideColorPicker(scrollbarContainerLeft)) {
      this.setState({ showColorPicker: false });
    }
  }, 30);

  componentDidMount() {
    window.addEventListener('mousedown', this.handleClickOutside);
    window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    window.removeEventListener('mousedown', this.handleClickOutside);
    window.removeEventListener('scroll', this.handleScroll);
  }

  getPopover = () => {
    const { disableColorPicker, item } = this.props;
    const { placement } = this.state;
    return (
      <Popover
        id={`comparisonCardHoldingIcon-popover-${item._id}`}
        className={classNames('popover-color-picker legacy-popover', { 'is-disabled': disableColorPicker })}
        data-testid="popover-color-picker"
        onKeyDown={this.handlePopoverKeyDown}
      >
        <Popover.Body>
          <div ref={this.popoverRef} className="popover-content">
            <div ref={this.anchorRef} className={classNames('anchor', placement)} />
            <ColorPicker item={item} onChange={this.props.updateComparisonItemColor} />
          </div>
        </Popover.Body>
      </Popover>
    );
  };

  handleClickOutside = (event: Event) => {
    if (!(event.target instanceof Node)) {
      return;
    }
    const ref = this.popoverRef.current;
    if (ref && !ref.contains(event.target)) {
      this.hideColorPicker();
    }
  };

  handlePopoverKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === VALUE_ESCAPE) {
      this.hideColorPicker();
      this.holdingIconRef.current?.focus();
    }
  };

  hideColorPicker = () => {
    this.setState({ showColorPicker: false });
  };

  setPopoverPlacement = () => {
    const elementRef = this.holdingIconRef.current;

    if (elementRef === null || this.popoverRef.current === null || this.anchorRef.current === null) {
      return;
    }

    const { offsetHeight: popoverHeight } = this.popoverRef.current;
    const { top: elementTop } = elementRef.getBoundingClientRect();
    const { offsetHeight: anchorHeight } = this.anchorRef.current;
    const topBreakPoint = elementTop - anchorHeight;

    if (popoverHeight <= topBreakPoint) {
      this.setState({ placement: 'top' });
    } else if (popoverHeight > topBreakPoint) {
      this.setState({ placement: 'bottom' });
    }
  };

  showColorPicker = (event: React.MouseEvent) => {
    const { disableColorPicker } = this.props;

    if (disableColorPicker) {
      return;
    }

    this.setState({ showColorPicker: true });
    event.stopPropagation();
  };

  render() {
    const { item, className, disableColorPicker, popoverContainerRef, ...rest } = this.props;
    const { placement, showColorPicker } = this.state;

    return (
      <OverlayTrigger container={popoverContainerRef} overlay={this.getPopover()} placement={placement} show={showColorPicker}>
        <InvisibleButton
          ariaLabel={formatMessage('Comparison.HoldingIconColorPicker')}
          onClick={this.showColorPicker}
          disabled={disableColorPicker}
          className={classNames('comparison-card-holding-icon', className)}
          ref={this.holdingIconRef}
        >
          <Tooltip className="custom-tooltip-v2" message={formatMessage('Comparison.EditColor')} disabled={disableColorPicker}>
            <span>
              <HoldingIcon type={item.type} color={item.color} />
            </span>
          </Tooltip>
        </InvisibleButton>
      </OverlayTrigger>
    );
  }
}

export default LegacyComparisonCardHoldingIcon;
